import { useState, useEffect } from 'react';
import { getOrderDates, createOrderDates, deleteOrderDate } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Save } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { ja } from 'date-fns/locale';

export const OrderDates = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [pendingChanges, setPendingChanges] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await getOrderDates();
      setDates(res.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getMonthDays = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const start = startOfMonth(new Date(year, month - 1));
    const end = endOfMonth(new Date(year, month - 1));
    return eachDayOfInterval({ start, end });
  };

  const getDateStatus = (dateStr) => {
    if (pendingChanges[dateStr] !== undefined) {
      return pendingChanges[dateStr];
    }
    const found = dates.find(d => d.date === dateStr);
    return found ? found.is_available : true; // デフォルトは発注可能
  };

  const toggleDate = (dateStr) => {
    const currentStatus = getDateStatus(dateStr);
    setPendingChanges({ ...pendingChanges, [dateStr]: !currentStatus });
  };

  const handleSave = async () => {
    try {
      const updates = Object.entries(pendingChanges).map(([date, is_available]) => ({
        date,
        is_available,
        note: ''
      }));
      
      if (updates.length === 0) {
        toast.info('変更がありません');
        return;
      }

      await createOrderDates(updates);
      toast.success('保存しました');
      setPendingChanges({});
      fetchData();
    } catch (error) {
      toast.error('保存に失敗しました');
    }
  };

  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  const monthDays = getMonthDays();
  const firstDayOfWeek = monthDays[0].getDay();

  return (
    <div className="space-y-6 fade-in" data-testid="order-dates-page">
      <div className="page-header">
        <h1 className="page-title">配送可能日マスタ</h1>
        <Button onClick={handleSave} disabled={Object.keys(pendingChanges).length === 0}>
          <Save className="h-4 w-4 mr-2" />
          変更を保存
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              カレンダー
            </CardTitle>
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-40"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-100 border border-emerald-300 rounded"></div>
              <span>配送可能</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
              <span>配送不可</span>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {dayNames.map((day, i) => (
              <div key={day} className={`text-center p-2 font-medium text-sm ${i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : ''}`}>
                {day}
              </div>
            ))}
            
            {/* 月初の空白 */}
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="p-2"></div>
            ))}
            
            {monthDays.map((date) => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const isAvailable = getDateStatus(dateStr);
              const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
              const hasChange = pendingChanges[dateStr] !== undefined;
              const dayOfWeek = date.getDay();
              
              return (
                <button
                  key={dateStr}
                  onClick={() => !isPast && toggleDate(dateStr)}
                  disabled={isPast}
                  className={`
                    p-2 text-center rounded transition-colors relative
                    ${isPast ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}
                    ${isAvailable ? 'bg-emerald-100 border border-emerald-300' : 'bg-red-100 border border-red-300'}
                    ${hasChange ? 'ring-2 ring-blue-500' : ''}
                    ${dayOfWeek === 0 ? 'text-red-600' : dayOfWeek === 6 ? 'text-blue-600' : ''}
                  `}
                >
                  <span className="text-sm font-medium">{format(date, 'd')}</span>
                </button>
              );
            })}
          </div>

          {Object.keys(pendingChanges).length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                {Object.keys(pendingChanges).length}件の変更があります。「変更を保存」ボタンで保存してください。
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">発注不可日一覧</CardTitle>
        </CardHeader>
        <CardContent>
          {dates.filter(d => !d.is_available).length === 0 ? (
            <p className="text-slate-500 text-center py-4">発注不可日は設定されていません</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {dates.filter(d => !d.is_available).sort((a, b) => a.date.localeCompare(b.date)).map((d) => (
                <Badge key={d.date} variant="outline" className="bg-red-50 text-red-700">
                  {format(new Date(d.date), 'yyyy年MM月dd日(E)', { locale: ja })}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
